'use client';

import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

type AuthUser = {
  twitterProfileId?: string | null;
  twitterUsername?: string | null;
  username?: string | null;
};

export type RelationshipType = 'mutual' | 'following' | 'followed_by' | 'none';

interface RelationshipBadgeProperties {
  className?: string;
  size?: 'xs' | 'sm';
  subjectId?: string | null;
  subjectUsername?: string | null;
  type: RelationshipType;
}

function labelFor(type: Exclude<RelationshipType, 'none'>): string {
  switch (type) {
    case 'mutual': {
      return 'Mutual';
    }
    case 'following': {
      return 'Following';
    }
    case 'followed_by': {
      return 'Follows you';
    }
    default: {
      return 'Follows you';
    }
  }
}

function colorFor(type: Exclude<RelationshipType, 'none'>): string {
  switch (type) {
    case 'mutual': {
      return 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-success/40 dark:bg-success/15 dark:text-success-foreground';
    }
    case 'following': {
      return 'border border-sky-200 bg-sky-50 text-sky-700 dark:border-info/40 dark:bg-info/10 dark:text-info-foreground';
    }
    case 'followed_by': {
      return 'border border-amber-200 bg-amber-50 text-amber-700 dark:border-muted/50 dark:bg-muted/20 dark:text-muted-foreground';
    }
    default: {
      return 'border border-slate-200 bg-slate-50 text-slate-600 dark:border-muted/50 dark:bg-muted/20 dark:text-muted-foreground';
    }
  }
}

export function RelationshipBadge({
  type,
  className,
  size = 'xs',
  subjectId,
  subjectUsername,
}: RelationshipBadgeProperties) {
  const { user } = useAuth();
  const isAuthUserObject = user && typeof user === 'object';
  const authUser = isAuthUserObject ? (user as AuthUser) : null;
  const viewerProfileId = typeof authUser?.twitterProfileId === 'string' ? authUser.twitterProfileId : null;
  const viewerUsernameSource = (() => {
    if (typeof authUser?.twitterUsername === 'string') {
      return authUser.twitterUsername;
    }
    if (typeof authUser?.username === 'string') {
      return authUser.username;
    }
    return null;
  })();
  const viewerUsername = viewerUsernameSource ? viewerUsernameSource.toLowerCase() : null;

  const normalizedSubjectUsername = subjectUsername?.toLowerCase() ?? null;
  const isSelf = Boolean(
    (subjectId && viewerProfileId && subjectId === viewerProfileId) ||
    (normalizedSubjectUsername && viewerUsername && normalizedSubjectUsername === viewerUsername)
  );

  if (type === 'none' || isSelf) {
    return null;
  }

  const base = size === 'xs' ? 'px-1.5 py-0.5 text-[10px] rounded-md' : 'px-2 py-0.5 text-xs rounded-md';
  const color = colorFor(type);

  return <span className={cn(base, color, className)}>{labelFor(type)}</span>;
}
